// @flow
import * as Cookie from 'app/packages/cookie-utils';
import parseUri from 'app/packages/url/parseUri.external.js'; // eslint-disable-line import/extensions
import templateConst from 'app/common/lib/templateConst';

type Location = {
  pathname: string,
  search: '?' | string,
};

const COOKIE_PREFIX = 'offsite_';
export const UrlClass = {
  SAME_ORIGIN: 1,
  TRUSTED_DIFFERENT_ORIGIN: 2,
  UNTRUSTED: 3,
  INVALID: 4,
};
const decode = (str) => {
  if (!str) {
    return '';
  }
  return decodeURIComponent(str.replace(/\+/g, ' '));
};

export const currentUrl = () => {
  const { pathname, search }: Location = window.location;
  return pathname + (search === '?' ? '' : search);
};

export const parseHostPath = (url: string) => url.split('?')[0];

export const parseQuery = (search: ?string): { [key: string]: string } => {
  if (!search) {
    return {};
  }
  if (search[0] === '?') {
    search = search.substr(1);
  }
  return search.split('&').reduce((query, str) => {
    const [key, val] = str.split(/=(.+)/);
    query[decode(key)] = decode(val);
    return query;
  }, {});
};

export const buildQuery = (queryDict: { [string]: any }): string =>
  Object.keys(queryDict)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(queryDict[key])}`)
    .join('&');

export const appendQuery = (url: string, params: Object) => {
  if (url.includes('?')) {
    return `${url}&${buildQuery(params)}`;
  } else {
    return `${url}?${buildQuery(params)}`;
  }
};

export const removeQueryParams = (url: string, blacklistedParams: Array<string>): string => {
  if (blacklistedParams.length === 0) {
    return url;
  }

  const [host, search] = url.split('?');
  const queryDict = Object.entries(parseQuery(search))
    .filter(([key, value]) => !blacklistedParams.includes(key))
    .reduce(
      (filteredQueryDict, [key, value]) => ({
        ...filteredQueryDict,
        [key]: value,
      }),
      {},
    );

  return appendQuery(host, queryDict);
};

export const getSafeLink = (url: string, queryParams?: { [string]: string | number }) => {
  const cookieKey = Math.round(Math.random() * 1000) + '';
  const cookieValue = Math.round(Math.random() * 1000) + '';
  Cookie.set(COOKIE_PREFIX + cookieKey, cookieValue);

  const token = `${cookieKey}-${cookieValue}`;
  const query = Object.assign({ token, url }, queryParams);
  return `/offsite/?${buildQuery(query)}`;
};

export const openSafeLink = (url: string, queryParams?: { [string]: string | number }) => {
  window.open(getSafeLink(url, queryParams), '_blank');
};

type ParsedUri = {
  anchor: string,
  authority: string,
  directory: string,
  file: string,
  host: string,
  password: string,
  path: string,
  port: string,
  protocol: 'http' | 'https' | '' | string,
  query: string,
  queryKey: Object,
  relative: string,
  source: string,
  user: string,
  userInfo: string,
};

export type ParsedUrl = ParsedUri & {
  origin: string | null,
  hostname: string | null,
};

export const parse = function(url: string, noWindow?: boolean): ParsedUrl {
  // Because this util is required by Node and browsers,
  // we can't rely on the DOM API A element to do the
  // parsing for us because we can't reference `document`
  let origin, hostname;
  const parsed: ParsedUri = parseUri(url);
  // Make this URL absolute
  switch (parsed.protocol) {
    case 'http':
    case 'https':
      origin = parsed.protocol + '://' + parsed.authority;
      hostname = parsed.host;
      break;
    case '': // Protocol-less or Relative URL
      if (parsed.source.indexOf('//') === 0) {
        if (noWindow) {
          // If we don't have access to window.location, we can't
          // verify the url origin
          origin = null;
        } else {
          origin = window.location.protocol + '//' + parsed.authority;
        }
        hostname = parsed.host;
      } else if (noWindow) {
        // If we don't have access to window.location, we can't
        // return a relative url's origin or hostname
        origin = null;
        hostname = null;
      } else {
        origin = window.location.protocol + '//' + window.location.hostname;
        hostname = window.location.hostname;
      }
      break;
    default:
      // URL starts with a non-supported protocol
      origin = null;
      hostname = parsed.host;
  }

  return { ...parsed, origin, hostname };
};

export const getUrlClass = function(url: string): number {
  const urlParsed = parse(url);
  if (urlParsed.origin === window.location.origin) {
    return UrlClass.SAME_ORIGIN;
  }

  if (urlParsed.hostname === window.location.hostname) {
    return UrlClass.TRUSTED_DIFFERENT_ORIGIN;
  }

  if (urlParsed.hostname) {
    const trustedEnding = '.' + templateConst.settings.TRUSTED_HOSTNAME_ENDING;
    // trustedEnding will look like "foo.com".  We want to consider both "foo.com" and
    // "sub.foo.com" as trusted.
    const host = urlParsed.hostname;
    const trustedSubhost = host.includes(trustedEnding, host.length - trustedEnding.length);
    if (urlParsed.hostname === trustedEnding || trustedSubhost) {
      return UrlClass.TRUSTED_DIFFERENT_ORIGIN;
    }
  }

  if (urlParsed.origin && urlParsed.origin === templateConst.settings.AUTHENTICATION_ORIGIN) {
    return UrlClass.TRUSTED_DIFFERENT_ORIGIN;
  }

  if (urlParsed.origin) {
    return UrlClass.UNTRUSTED;
  }
  return UrlClass.INVALID;
};

export const isTrustedOrigin = (url: string): boolean =>
  [UrlClass.SAME_ORIGIN, UrlClass.TRUSTED_DIFFERENT_ORIGIN].includes(getUrlClass(url));

export default undefined;
